import React from 'react'
import DOMPurify from 'dompurify'
import parse, { domToReact } from "html-react-parser"
import {
  WingsHeading,
  WingsText,
} from '@wingspans-ui'
import TrackedLink from './components/links/TrackedLink'

/*
  Utilities to replace carriage returns to <br>.
  Use this utils to format some data that was pushed to Cosmic via apis and if it contains line breaks.
*/

const convertNewLineToBR = str => str.replace(/(?:\r\n|\r|\n)/g, "<br>")

const formatText = str => `<p>${convertNewLineToBR(str)}</p>`

/**
 * Convert html string to plain text. To maintain formatting, replacing opening
 * and closing br, div, p, and li tags with newlines. All other html tags are
 * just removed.
 * @param {string} html
 * @returns {string}
 */
const htmlToTextWithBreaks = (html) => {
  if (!DOMPurify?.sanitize) {
    return ""
  }
  // Sanitize input first
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['br', 'div', 'p', 'li'],
    KEEP_CONTENT: true,
  })

  // Now regex is safe since input is pre-cleaned
  const text = clean
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/?(div|p|li)[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '') // Safe now - DOMPurify ensures valid HTML
    .replace(/\n{2,}/g, '\n\n')

  // Safer entity decoding
  const parser = new DOMParser()
  const doc = parser.parseFromString(text, 'text/html')
  return doc.body.textContent.trim()
}

/**
 * Convert HTML into React JSX that uses our style system. Currently only used
 * company page copy downloaded from Cosmic. Makes the following conversions:
 *  <p> --> <WingsText> (for non-terminal paragraphs, margin bottom is set to w-spacing-1)
 *  <a> --> <WingsLink> (with click tracking on relevant pages)
 *  <li> --> <li><WingsText></li>
 *  <p><strong><p> --> <WingsHeading> (level 5)
 *  <ul> --> <ul> with margin-bottom of 0
 * @param {string} html 
 * @returns {JSX}
 */
const convertHtmlToWingsUiComponents = (html) => {
  const htmlReactParserOptions = {
    replace(node) {
      if (node.type !== "tag") {
        return
      } else if (node.name === "p") {
        const usedAsHeading = node.children.length === 1
                              && node.children[0].type === "tag"
                              && node.children[0].name === "strong"
        if (usedAsHeading) {
          return (
            <WingsHeading level={5} className="w-mb-1">
              {domToReact(node.children, htmlReactParserOptions)}
            </WingsHeading>
          )
        } else return (
          <WingsText className={!!node.next ? "w-mb-1" : ""}>
            {domToReact(node.children, htmlReactParserOptions)}
          </WingsText>
        )
      } else if (node.name === "a") {
        const { href } = node.attribs
        return (
          <TrackedLink to={href} isExternal={true} isNewTab={true}>
            {domToReact(node.children, htmlReactParserOptions)}
          </TrackedLink>
        )
      } else if (node.name === "li") {
        return (
          <li>
            <WingsText>
              {domToReact(node.children, htmlReactParserOptions)}
            </WingsText>
          </li>
        )
      } else if (node.name === "ul") {
        return (
          <ul className="w-mb-0">
            {domToReact(node.children, htmlReactParserOptions)}
          </ul>
        )
      }
      return
    },
  }
  return <>{parse(html, htmlReactParserOptions)}</>
}

const storyReadingOptions = {
  standard: {
    text: "Standard",
    value: "standard",
    textEs: "Estándar",
  },
  simplified: {
    text: "Quick Read",
    value: "simplified",
    textEs: "Lectura rápida",
  },
}
const demandTranslations = {
  'Stable Demand': {
    'en-US': 'Stable',
    'es-ES': 'Estable'
  },
  'Increasing Demand': {
    'en-US': 'Increasing',
    'es-ES': 'Creciente'
  },
  'Declining Demand': {
    'en-US': 'Declining',
    'es-ES': 'Declinante'
  },
  'High Demand': {
    'en-US': 'High',
    'es-ES': 'Alta'
  },
  'Low Demand': {
    'en-US': 'Low',
    'es-ES': 'Baja'
  }
};

/**
 * Picks a font color (#FFFFFF or #363636) by calculating whether #FFFFFF has
 * enough contrast with *backgroundColor*
 * @param {string} backgroundColor - color in hex code
 */
const pickAccessibleTextColor = (backgroundColor) => {
  // WCAG relative luminance formula: https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
  const rSRGB = Number(`0x${backgroundColor.slice(1, 3)}`) / 255
  const gSRGB = Number(`0x${backgroundColor.slice(3, 5)}`) / 255
  const bSRGB = Number(`0x${backgroundColor.slice(5, 7)}`) / 255
  const r = (rSRGB <= 0.03928) ? (rSRGB / 12.92) : (((rSRGB + 0.055) / 1.055) ** 2.4)
  const g = (gSRGB <= 0.03928) ? (gSRGB / 12.92) : (((gSRGB + 0.055) / 1.055) ** 2.4)
  const b = (bSRGB <= 0.03928) ? (bSRGB / 12.92) : (((bSRGB + 0.055) / 1.055) ** 2.4)
  const backgroundRelativeLuminance = (0.2126 * r) + (0.7152 * g) + (0.0722 * b)

  // WCAG contrast ratio formula: https://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef
  const whiteRelativeLuminance = 1 // #FFFFFF
  const whiteContrastRatio = (whiteRelativeLuminance + 0.05) / (backgroundRelativeLuminance + 0.05)
  return (whiteContrastRatio < 3) ? "#363636" : "#FFFFFF"
}

/**
 * Convert a css variable to its value. If *cssVar* is not formatted correctly,
 * return it. If the document or variable is not defined, return ""
 * @param {string} cssVar - in the form var(<name of variable>)
 * @returns {string}
 */
const resolveCssVariable = (cssVar) => {
  const variableName = cssVar.match(/var\((--[^)]+)\)/)?.[1]

  if (!variableName) return cssVar

  if (!document) {
    return ""
  }

  return getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim()
}

export { convertNewLineToBR, formatText, htmlToTextWithBreaks, convertHtmlToWingsUiComponents, storyReadingOptions, demandTranslations, pickAccessibleTextColor, resolveCssVariable }
